import {AhpProvider} from './providers/AhpProvider'
import {EstablishedAhpSessionProvider} from '@github-ui/copilot-shared-ui/components/EstablishedAhpSessionProvider'
import {useCopilotResultPresentationSelection} from '@github-ui/copilot-browser-latency/use-copilot-result-presentation'
import type {
  AhpSpaceSelection,
  AhpSubmissionMetadata,
  EstablishedAhpSession,
  PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import type {EstablishedAhpInitialSubmissionCoordinator} from '@github-ui/ahp-session-client/initial-submission'
import type {ConversationDeletion} from './runtime/use-conversation-deleted-navigation'
import type {SubmissionFailure} from '@github-ui/ahp-session-client/submission-error'
import type {ChatState} from '@microsoft/agent-host-protocol'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {AhpSessionIdentifiers, ChatDisplayIdentity, DebugEvent, RuntimeStatus} from './types'
import type {AnchorSide} from '@primer/behaviors'
import {isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'
import {useState} from 'react'

import {ChatShell, type ChatShellLayoutRenderer} from './ChatApp/ChatShell'
import type {EmptyStateMode} from '@github-ui/one-copilot-composer'

export interface ChatAppProps {
  initialAhpState?: ChatState
  initialDebugEvents?: DebugEvent[]
  debugEvents?: readonly DebugEvent[]
  initialRuntimeStatus?: RuntimeStatus
  establishedAhpSession?: EstablishedAhpSession
  establishedAhpSessionPending?: boolean
  establishedAhpSessionError?: string
  /**
   * A first turn the host dispatched outside the composer (one composed on
   * another page) that failed. Reported on the composer, with the composed text
   * restored, because nothing on this surface would otherwise show it.
   */
  submissionFailure?: SubmissionFailure
  /**
   * A first turn dispatched by the host that should be shown while it provisions,
   * so a turn composed elsewhere appears as the user's own rather than behind a
   * generic loading state.
   */
  pendingInitialPrompt?: PreparedPrompt
  /** Renders the composer alone, for a surface that starts a chat but never hosts one. */
  composerOnly?: boolean
  initialSubmission?: EstablishedAhpInitialSubmissionCoordinator
  initialPrompt?: PreparedPrompt
  /** Structured context captured with an entry-point first turn. */
  initialSubmissionMetadata?: AhpSubmissionMetadata
  initialSelectedSpace?: AhpSpaceSelection
  initialSelectedMode?: AgentMode
  initialSubmissionOrigin?: 'new' | 'recovery'
  onSessionEstablished?: (session: EstablishedAhpSession) => void
  onSessionRenamed?: (taskId: string, title: string) => void
  onUserRename?: () => void
  ahpSession?: AhpSessionIdentifiers
  /** Keeps replayed queued messages non-interactive until the persisted session connects. */
  dormantQueuedMessagesReadOnly?: boolean
  /** Supplies session identity without activating the relay-backed timeline. */
  displaySession?: ChatDisplayIdentity
  /** Controls which side of the anchor the attach menu overlay opens on. */
  attachMenuSide?: AnchorSide
  /** When true, the internal Chat header (title + debug button) is hidden. */
  hideHeader?: boolean
  /** Fired once after the established first turn is accepted. */
  onSessionCreated?: (taskId: string) => void
  conversationDeletion?: ConversationDeletion
  /**
   * Which empty-state surface to render before the first turn: `'immersive'`
   * for the centered home layout (Copilot animation, composer, icebreakers).
   * Undefined renders no empty state.
   */
  emptyState?: EmptyStateMode
  renderLayout?: ChatShellLayoutRenderer
  /** Forces a non-composable shell, e.g. while the host's chat quota is exhausted. */
  readOnly?: boolean
  /** Suppresses the composer's mount-time autofocus. */
  disableAutoFocus?: boolean
}

export function ChatApp({
  initialAhpState,
  initialDebugEvents,
  debugEvents,
  initialRuntimeStatus,
  establishedAhpSession,
  establishedAhpSessionPending,
  establishedAhpSessionError,
  submissionFailure,
  pendingInitialPrompt,
  composerOnly,
  initialSubmission,
  initialPrompt,
  initialSelectedMode,
  initialSubmissionMetadata,
  initialSelectedSpace,
  initialSubmissionOrigin,
  onSessionEstablished,
  onSessionRenamed,
  onUserRename,
  ahpSession,
  dormantQueuedMessagesReadOnly,
  displaySession,
  attachMenuSide,
  hideHeader,
  onSessionCreated,
  conversationDeletion,
  emptyState,
  renderLayout,
  readOnly,
  disableAutoFocus,
}: ChatAppProps) {
  const [initialPromptConsumed, setInitialPromptConsumed] = useState(false)
  const resultPresentation = useCopilotResultPresentationSelection(
    composerOnly ? undefined : (establishedAhpSession?.identity ?? ahpSession),
    'chat_app',
  )
  const handleSessionEstablished = (session: EstablishedAhpSession) => {
    if (!composerOnly) resultPresentation?.select(session.identity)
    onSessionEstablished?.(session)
    if (session.identity.taskId) onSessionCreated?.(session.identity.taskId)
  }

  const chatShell = (
    <ChatShell
      ahpSession={ahpSession}
      dormantQueuedMessagesReadOnly={dormantQueuedMessagesReadOnly}
      displaySession={displaySession}
      attachMenuSide={attachMenuSide}
      hideHeader={hideHeader}
      conversationDeletion={conversationDeletion}
      shipFeedbackEnabled={isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')}
      environmentSetupEnabled
      establishedAhpSessionPending={establishedAhpSessionPending}
      establishedAhpSessionError={establishedAhpSessionError}
      submissionFailure={submissionFailure}
      pendingInitialPrompt={pendingInitialPrompt}
      composerOnly={composerOnly}
      initialSubmission={initialSubmission}
      initialPrompt={initialPromptConsumed ? undefined : initialPrompt}
      initialSubmissionMetadata={initialSubmissionMetadata}
      initialSelectedSpace={initialSelectedSpace}
      onInitialPromptDispatched={() => setInitialPromptConsumed(true)}
      initialSubmissionOrigin={initialSubmissionOrigin}
      onSessionEstablished={handleSessionEstablished}
      emptyState={emptyState}
      renderLayout={renderLayout}
      readOnly={readOnly}
      disableAutoFocus={disableAutoFocus}
    />
  )

  return (
    <AhpProvider
      initialDebugEvents={initialDebugEvents}
      debugEvents={debugEvents}
      initialRuntimeStatus={initialRuntimeStatus}
      initialAhpState={initialAhpState}
      initialSelectedSpace={initialSelectedSpace}
      initialSelectedMode={initialSelectedMode}
      onSessionRenamed={onSessionRenamed}
      onUserRename={onUserRename}
    >
      <EstablishedAhpSessionProvider
        session={establishedAhpSession}
        resultPresentation={resultPresentation}
        recoveryControls
      >
        {chatShell}
      </EstablishedAhpSessionProvider>
    </AhpProvider>
  )
}

ChatApp.displayName = 'ChatApp'
